'use client';

import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { buttonVariants } from '@paypoteam/ui-components/Button';
import Link from 'next/link';

import { partnerPageGA, shopReferral } from '@/lib/gtm';
import { cn } from '@/lib/utils';

import { LocalImage } from '@/components/atoms/LocalImage';
import { Title } from '@/components/atoms/Typography/Title';

import type { PromoCardConfigType } from '../PromoCardsSection.types';

export interface PromoCardProps extends ButtonHTMLAttributes<HTMLDivElement> {
    config: PromoCardConfigType;
    partnerName?: string;
    shopLinkPlacement?: string;
}

const PromoCard = forwardRef<HTMLDivElement, PromoCardProps>(
    ({ config, partnerName, shopLinkPlacement, className, ...props }, ref) => {
        const gtmEventHandler = () => {
            partnerPageGA(config.buttonTitle, partnerName, config.href);
            !!shopLinkPlacement && shopReferral(partnerName, shopLinkPlacement);
        };

        return (
            <div
                className={cn(
                    'relative h-52 w-full max-w-[350px] overflow-hidden rounded-2xl border border-grey-200 bg-background p-4',
                    'md:h-[280px] md:max-w-full md:p-8 lg:h-[350px]',
                    className,
                )}
                ref={ref}
                {...props}
            >
                <div className="z-10 flex max-w-[60%] flex-col md:h-full md:max-w-[70%] lg:max-w-52 lg:justify-center">
                    <Title
                        size="l"
                        className="z-10 text-left text-md md:text-lg"
                    >
                        {config.title}
                    </Title>

                    <Link
                        href={config.href}
                        onClick={gtmEventHandler}
                        className={cn(
                            buttonVariants(),
                            'absolute bottom-4 left-4 z-10 !w-fit min-w-[200px] md:bottom-8 md:left-8',
                        )}
                    >
                        {config.buttonTitle}
                    </Link>
                </div>

                <div className="absolute right-0 top-1/2 z-0 h-full w-full -translate-y-1/2">
                    <LocalImage
                        className="h-auto object-cover object-right"
                        fill
                        priority
                        {...config.image}
                    />
                </div>
            </div>
        );
    },
);

export default PromoCard;
